import { Updater } from "use-immer";
// mine:
import { AnyGameObj, AnyGameState } from "@/app/lib/game-state/game-types";
import Nav from "@/app/lib/input/nav";
import UI from "@/app/lib/ui/ui"
import WordModule from "@/app/lib/game-state/word-module";
import LetterModule from "@/app/lib/game-state/letter-module";

export default class InputHandler {
    wordIndex: number;
    letterPosition: number;
    game: AnyGameObj;
    ui: UI;
    nav: Nav;
    wordModule: WordModule;
    letterModule: LetterModule;

    constructor({
        wordIndex, letterPosition, game, ui
    } : {
        wordIndex: number,
        letterPosition: number,
        game: AnyGameObj,
        ui: UI
    }

    ) {
        this.wordIndex = wordIndex;
        this.letterPosition = letterPosition;
        this.game = game;
        this.ui = ui;

        this.nav = new Nav(
            wordIndex,
            letterPosition,
            game.state
        );

        this.wordModule = game.state.words[wordIndex];
        this.letterModule = this.wordModule.letters[letterPosition];
    }

    // Simple Moves For Arrow Inputs
    // -------------------------------
    goRight() {
        const right = this.nav.findRight();
        this.ui.focus(right);
    }
    goLeft() {
        const left = this.nav.findLeft();
        this.ui.focus(left);
    }
    goUp() {
        const up = this.nav.findUp();
        this.ui.focus(up);
    }
    goDown() {
        const down = this.nav.findDown();
        this.ui.focus(down);
    }

    // Moves that go forward/back through entry slots
    // ------------------------------------------
    goPrevEntry(useWrapping: boolean = true) {
        const prevEntrySlot = this.nav.findPreviousEntrySlot(useWrapping);
        this.ui.focus(prevEntrySlot);
    }
    goNextEntry(useWrapping: boolean = true) {
        const nextEntrySlot = this.nav.findNextEntrySlot(useWrapping);
        this.ui.focus(nextEntrySlot);
    }
    // useful for delaying input values -- sometimes when we don't delay, the frame holds an input we tried to plane into a given,
    // then we move to the next entry slot, and it enters there. it's a bit awkward
    goNextEntryDelay(useWrapping: boolean = true) {
        setTimeout(() => {
            this.goNextEntry(useWrapping)
        }, 0);
    }
    // have this here in case we need it too:
    goPrevEntryDelay(useWrapping: boolean = true) {
        setTimeout(() => {
            this.goPrevEntry(useWrapping)
        }, 0);
    }
    goWordStart() {
        const firstEntrySlot = this.nav.findFirstEntrySlot();
        this.ui.focus(firstEntrySlot);
    }
    goWordEnd() {
        const lastEntrySlot = this.nav.findLastEntrySlot();
        this.ui.focus(lastEntrySlot);
    }

    // This brings us to the first empty slot of the next unsolved word
    // it skips this word
    // useful when solving a word
    goNextUnblockedWord(gameState: AnyGameState) {
        // let's prevent an infinite loop
        if (gameState.victorious)
            return;

        // set to the current word as a safety check
        // it should always get reassigned later as there should be an unsolved word
        let targetWordModule: WordModule = this.wordModule;

        // loop through the words, starting at 1 + our arbitrary position, and wrapping around as needed
        // we start with i = 1 so that we skip the current word
        for (let i = 1; i < gameState.words.length; i++) {
            // start at this.wordIndex + 1, note modular arithmetic
            const modIndex = (this.wordIndex + i) % gameState.words.length;
            const candidateWordModule: WordModule = gameState.words[modIndex];
            if (candidateWordModule.blockComplexNav() === false) {
                targetWordModule = candidateWordModule;
                break;
            }
        }
        // initialize a new nav to the target word / any letter pos
        const targetNav: Nav = new Nav(targetWordModule.index, 0, gameState);
        // get the first entry slot
        const targetFirstEntrySlot = targetNav.findFirstEntrySlot();
        // focus there
        this.ui.focus(targetFirstEntrySlot);
    }

    // Affects board state:
    // --------------------------------------
    // shuffles the ingredients
    shuffle() {
        this.game.update(state => state.shuffle());
    }
    // clears the box
    clear() {
        this.game.update(state => state.words[this.wordIndex].setEntry(this.letterPosition, ''));
    }

    fill(character: string) {
        // a flag for our animations
        let animationFlag: 'reject' | 'accept' | null = null;
        this.game.update(gameState => {
            gameState.words[this.wordIndex].setEntry(this.letterPosition, character);
            // if the word is complete, we make a call to validate
            if (gameState.words[this.wordIndex].isComplete()) {
                // if it's true, we advance to next unblocked word
                if (gameState.validateWord(this.wordIndex)) {
                    this.goNextUnblockedWord(gameState as AnyGameState); // dirty solution for draft vs original type
                    animationFlag = 'accept';
                }
                // if it's false, we wipe entries and go back to start
                else {
                    gameState.words[this.wordIndex].wipeEntries();
                    /*
                    OLD BEHAVIOR
                    TAKING OUT FOR NOW, BECAUSE ANIMATIONS GIVE FEEDBACK INSTEAD

                    // only wipe if there's more than one entry slot
                    if (gameState.words[this.wordIndex].getLettersByType('entry').length > 1) {
                        gameState.words[this.wordIndex].wipeEntries();
                    }
                    // if we're at the first slot, fill it in again (overwrite the wipe)
                    if (this.letterPosition === this.nav.findFirstEntrySlot().letterPosition) {
                        gameState.words[this.wordIndex].setEntry(this.letterPosition, character);
                        this.goNextEntry();
                    }*/
                    
                    animationFlag = 'reject';
                    this.goWordStart();
                }
            }
            // if it's not complete we just advance
            else {
                this.goNextEntry(false);
            }

            // process animations
            // on a delay to avoid some warnings we're getting about state conflicts
            // (the flags should be enough I thought!)
            setTimeout(() => {
                if (animationFlag === 'reject') {
                    this.ui.rejectAnimation(this.wordIndex);
                } else if (animationFlag === 'accept') {
                    this.ui.solveAnimation(this.wordIndex);
                }
            }, 0);     
        });
    }

    // clears word and goes back to start
    clearWord() {
        this.game.update(gameState => {
            if (!gameState.words[this.wordIndex].solved) {
                gameState.words[this.wordIndex].wipeEntries();
            }
            this.goWordStart();
        });
    }
    
    // Behaves like a backspace
    backspace() {
        // if the slot is empty or a given, we just go backwards
        // but don't wrap if we're the earliest unsolved word
        
        // first let's find out if we're the earliest unsolved word
        let isEarliestUnsolvedWord: boolean = false;
        for (const wordModule of this.game.state.words) {
                // proceed until we find an unsolved word
                if (wordModule.solved) {
                    continue;
                }
                // at the first unsolved word, check if it's the word we're on
                if (wordModule.index === this.wordIndex) {
                    isEarliestUnsolvedWord = true;
                }
                // regardless of if it is or isn't, we're done here
                break;
        }

        if (
            this.game.state.getLetter(this.wordIndex, this.letterPosition) === ''
            || this.letterModule.type === 'given'
        ) {
            // only wrap if we're not the earliest unsolved word
            this.goPrevEntry(!isEarliestUnsolvedWord);
        }
        // if the slot is filled, and an entry, clear it
        if (
            this.game.state.getLetter(this.wordIndex, this.letterPosition) !== ''
            && this.letterModule.type === 'entry'
        ) {
            this.clear()
            // go back, same wrapping rules w/ earliest word
            this.goPrevEntry(!isEarliestUnsolvedWord);
        }
    }
}